import type { ReactNode } from "react"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { metricIconFor } from "@/metrics/catalog"
import { Badge } from "@coval/ui"
import {
  Bell,
  BotMessageSquare,
  ChartNoAxesCombined,
  ClipboardCheck,
  Code,
  FileText,
  Folder,
  LayoutDashboard,
  MessagesSquare,
  UserRound,
  type LucideIcon,
} from "lucide-react"

import { MessageSquareArrowUp } from "@/components/icons/MessageSquareArrowUp"
import { useResolveReviewer } from "@/hooks/useResolveReviewer"
import { normalizeConversationSourceFilter } from "@/types/conversationSource"
import { LANGUAGE_DISPLAY_MAP } from "@/types/ModelConfigs/LanguageDisplayNames"

import type { SofiaProposedAction } from "../model/types"
import {
  AGENT_CONNECTION_LABELS,
  createAgentConnectionType,
} from "../utils/agentActionConfig"
import { getCreationResource } from "../utils/creationActions"
import { titleCase } from "../utils/titleCase"
import { DraftTestCasesSummary } from "./DraftTestCasesSummary"
import { ReportConversationSummary } from "./ReportConversationSummary"

type SummaryStatus = "proposed" | "done" | "dismissed"
const RESOURCE_ICONS: Record<string, LucideIcon> = {
  persona: ENTITY_ICONS.personas,
  agent: ENTITY_ICONS.agents,
  "test set": ENTITY_ICONS["test-sets"],
  metric: ENTITY_ICONS.metrics,
  dashboard: LayoutDashboard,
  report: FileText,
  widget: ChartNoAxesCombined,
  "review project": ClipboardCheck,
  alert: Bell,
  "Python metric draft": Code,
}
const DESCRIPTION_KINDS = new Set([
  "create_test_set",
  "create_metric",
  "create_python_metric_draft",
  "publish_python_metric",
  "save_python_analysis_metric",
  "create_agent",
  "create_alert",
  "create_monitor",
  "create_dashboard",
  "build_dashboard",
  "create_review_project",
])

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? [...new Set(value.map(text).filter(Boolean))]
    : []
}

function humanizeType(value: string): string {
  return titleCase(
    value
      .replace(/^METRIC_|^manager_/i, "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b(llm|sql|api|stt|tts)\b/g, (word) => word.toUpperCase()),
  )
}

function Metadata({ label, children }: { label: string; children: ReactNode }) {
  return (
    <dl className="grid grid-cols-[fit-content(40%)_minmax(0,1fr)] items-start gap-x-3">
      <dt className="text-xs leading-5 text-coval-text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-sm leading-5 text-coval-text">
        {children}
      </dd>
    </dl>
  )
}

function Assignees({ emails }: { emails: string[] }) {
  const resolveReviewer = useResolveReviewer()
  return (
    <Metadata label="Assignees">
      <ul className="space-y-2">
        {emails.map((email) => {
          const reviewer = resolveReviewer(email)
          return (
            <li key={email} className="flex items-start gap-2">
              <UserRound
                className="mt-0.5 size-4 shrink-0 text-coval-text-muted"
                aria-hidden="true"
              />
              <div className="min-w-0 break-words">
                <p>{reviewer.name}</p>
                {reviewer.name !== reviewer.email ? (
                  <p className="text-xs text-coval-text-muted">
                    {reviewer.email}
                  </p>
                ) : null}
              </div>
            </li>
          )
        })}
      </ul>
    </Metadata>
  )
}

function MetricType({ args }: { args: Record<string, unknown> }) {
  const manager = text(args.metric_manager)
  const type = text(args.metric_type)
  const textJudge = !manager && (!type || type.startsWith("METRIC_LLM_"))
  const label = textJudge
    ? type === "METRIC_LLM_NUMERICAL"
      ? "LLM Judge · Numeric"
      : type === "METRIC_LLM_CATEGORICAL"
        ? "LLM Judge · Categorical"
        : !type || type === "METRIC_LLM_BINARY"
          ? "LLM Judge · Pass / Fail"
          : humanizeType(type)
    : humanizeType(type || manager)
  const Icon = metricIconFor(textJudge ? "text-llm-judge" : manager)
  return (
    <Metadata label="Metric Type">
      <span className="inline-flex items-center gap-2">
        <Icon className="size-4 shrink-0" aria-hidden="true" />
        {label}
      </span>
    </Metadata>
  )
}

export function CreationActionSummary({
  proposal,
  status = "proposed",
  withTestCases = false,
}: {
  proposal: SofiaProposedAction
  status?: SummaryStatus
  withTestCases?: boolean
}) {
  const args = proposal.args
  const resource = getCreationResource(proposal)
  const config =
    args.config &&
    typeof args.config === "object" &&
    !Array.isArray(args.config)
      ? (args.config as Record<string, unknown>)
      : {}
  const name =
    [args.metric_name, args.display_name, args.name, args.title, config.name]
      .map(text)
      .find(Boolean) ?? `New ${resource?.label ?? "resource"}`
  const description = DESCRIPTION_KINDS.has(proposal.kind)
    ? text(args.description) || text(config.description)
    : ""
  const Icon = RESOURCE_ICONS[resource?.label ?? ""] ?? Folder
  const label =
    proposal.kind === "create_review_project"
      ? "Human Review Project"
      : titleCase(resource?.label ?? "Resource")
  const languageCode = text(args.language_code)
  const language =
    LANGUAGE_DISPLAY_MAP[languageCode as keyof typeof LANGUAGE_DISPLAY_MAP] ??
    languageCode
  const agentType =
    proposal.kind === "create_agent"
      ? createAgentConnectionType(args)
      : undefined
  const conversationSource =
    proposal.kind === "create_alert" || proposal.kind === "create_monitor"
      ? normalizeConversationSourceFilter(
          config.conversation_source ?? args.conversation_source ?? args.scope,
        )
      : undefined
  const ConversationIcon =
    conversationSource === "simulated"
      ? BotMessageSquare
      : conversationSource === "uploaded"
        ? MessageSquareArrowUp
        : MessagesSquare
  const runIds = proposal.kind === "create_report" ? strings(args.run_ids) : []
  const assignees =
    proposal.kind === "create_review_project" ? strings(args.assignees) : []
  // Catalog text-judge managers map their question to the saved prompt.
  const catalogPromptKey = config.question != null ? "question" : "prompt"
  const metricPrompt = text(args.metric_manager)
    ? config[catalogPromptKey]
    : args.prompt
  const metricTags = Array.isArray(args.tags)
    ? [...new Set(strings(args.tags).map((tag) => tag.toLowerCase()))]
    : []

  return (
    <div className="min-w-0 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-coval-text">
          <Icon className="size-4 shrink-0" aria-hidden="true" />
          {label}
        </span>
        <Badge
          variant={
            status === "done"
              ? "success"
              : status === "dismissed"
                ? "default"
                : "info"
          }
          className="text-xs"
        >
          {status === "done"
            ? withTestCases
              ? "SET CREATED"
              : "CREATED"
            : status === "dismissed"
              ? "DISMISSED"
              : "CREATE"}
        </Badge>
      </div>
      <div className="space-y-3 border-t border-coval-border pt-3">
        {proposal.kind === "create_metric" ? (
          <>
            <MetricType args={args} />
            {typeof args.judge_mode === "string" ? (
              <Metadata label="Judge mode">{args.judge_mode}</Metadata>
            ) : null}
            {typeof args.runtime_config === "object" &&
            args.runtime_config !== null
              ? Object.entries(args.runtime_config).map(([key, value]) => (
                  <Metadata
                    key={key}
                    label={
                      key === "model_version"
                        ? "Model"
                        : key === "thinking_enabled"
                          ? "Thinking"
                          : key
                    }
                  >
                    {value === null
                      ? "Default"
                      : typeof value === "boolean"
                        ? value
                          ? "Enabled"
                          : "Disabled"
                        : String(value)}
                  </Metadata>
                ))
              : null}
            {Array.isArray(args.tags) ? (
              <Metadata label="Tags (saved lowercase)">
                {metricTags.length ? metricTags.join(", ") : "None"}
              </Metadata>
            ) : null}
            {text(args.metric_manager)
              ? Object.entries(config)
                  .filter(
                    ([key]) =>
                      key !== catalogPromptKey ||
                      typeof metricPrompt !== "string",
                  )
                  .map(([key, value]) => (
                    <Metadata
                      key={key}
                      label={titleCase(key.replaceAll("_", " "))}
                    >
                      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-coval-text">
                        {typeof value === "string"
                          ? value
                          : JSON.stringify(value, null, 2)}
                      </pre>
                    </Metadata>
                  ))
              : null}
            {typeof metricPrompt === "string" ? (
              <details>
                <summary className="cursor-pointer text-xs text-coval-text-muted">
                  Full judge prompt
                </summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-coval-text">
                  {metricPrompt}
                </pre>
              </details>
            ) : null}
          </>
        ) : null}
        <h3 className="break-words text-base font-semibold text-coval-text">
          {name}
        </h3>
        {description ? (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-coval-text-muted">
            {description}
          </p>
        ) : null}
        {proposal.kind === "create_persona" && language ? (
          <Metadata label="Language">{language}</Metadata>
        ) : null}
        {agentType ? (
          <Metadata label="Type">{AGENT_CONNECTION_LABELS[agentType]}</Metadata>
        ) : null}
        {conversationSource ? (
          <Metadata label="Conversation Type">
            <span className="inline-flex items-center gap-2">
              <ConversationIcon
                className="size-4 shrink-0"
                aria-hidden="true"
              />
              {conversationSource === "all"
                ? "All Conversations"
                : titleCase(conversationSource)}
            </span>
          </Metadata>
        ) : null}
        {runIds.length ? (
          <Metadata label="Included Conversations">
            <ReportConversationSummary runIds={runIds} />
          </Metadata>
        ) : null}
        {assignees.length ? <Assignees emails={assignees} /> : null}
      </div>
      {proposal.kind === "create_test_set" &&
      Array.isArray(args.test_cases) &&
      args.test_cases.length > 0 ? (
        <div className="border-t border-coval-border pt-3">
          <DraftTestCasesSummary proposal={proposal} status={status} />
        </div>
      ) : null}
    </div>
  )
}
